// Mock data for the Workflow Inbox (main) page.
// This file is intentionally the single source of truth for the page's data
// and will be extended/refined in follow-up work.
import {
  ActorType,
  Facility,
  StepStatus,
  WorkflowBadgeStatus,
  WorkflowItem,
} from '#models';
import type { ExceptionItem } from '#models';

export const WORKFLOW_FACILITIES_MOCK: Facility[] = [{ id: 'okan', name: 'OKAN' }];

export const WORKFLOW_ASSETS_MOCK: string[] = ['Sameple Asset 1', 'Sample Asset 2', 'Sample Asset 3'];

export const WORKFLOW_ITEMS_MOCK: WorkflowItem[] = [
  {
    id: 'permit-2024-0081',
    title: 'Hot Work · Unit 4',
    badge: WorkflowBadgeStatus.AwaitingDecision,
    description: 'DIAG-2026-0047 · Production Allocation Diagnostic · Well Pad C-14',
    status: WorkflowBadgeStatus.AwaitingDecision,
    actionText:
      'Re-calibrate flow transmitter FT-2204 using reference meter FT-2205. Apply back-allocation factor 0.916 to Train 2 oil volumes retroactively for Jul 5–7, 2026. Estimated volume recovery: +142 bbl. Re-run allocation model post-calibration to confirm convergence within ±1%.',
    identity: {
      title: 'DIAG-2026-0047 · Production Allocation Diagnostic',
      area: 'Nigeria Mid-Africa (NMA)',
      supplementary: 'Well Pad C-14 · Train 2 separator · SCADA/historian',
      stream: 'OKA_PP Crude',
      product: 'Oil',
      exceptionType: 'Production Allocation Diagnostic',
      useCase: '',
      confidenceLevel: 0,
      confidenceStatement: '',
    },
    steps: [
      {
        number: 1,
        title: 'Data Ingestion & Validation',
        actor: ActorType.AIAgent,
        status: StepStatus.Done,
        sub: 'Completed · Jul 7, 2026 06:14 — Pulled SCADA, historian & meter data for Well Pad C-14. 3 tag gaps auto-filled via interpolation.',
      },
      {
        number: 2,
        title: 'Anomaly Detection',
        actor: ActorType.AIAgent,
        status: StepStatus.Done,
        sub: 'Completed · Jul 7, 2026 06:17 — Detected 8.4% oil allocation imbalance on Train 2 separator. Confidence: 94%. Flagged for investigation.',
      },
      {
        number: 3,
        title: 'Root Cause Analysis',
        actor: ActorType.AIAgent,
        status: StepStatus.Done,
        sub: 'Completed · Jul 7, 2026 06:31 — Probable cause: meter FT-2204 drift post-maintenance (Jul 5). Secondary hypothesis: separator carry-over at high GOR.',
      },
      {
        number: 4,
        title: 'Engineer Review & Validation',
        actor: ActorType.Human,
        status: StepStatus.Done,
        sub: 'Completed · Jul 7, 2026 09:45 — J. Smith confirmed FT-2204 meter drift. Rejected secondary hypothesis. Approved RCA summary for action planning.',
      },
      {
        number: 5,
        title: 'Corrective Action Generation',
        actor: ActorType.AIAgent,
        status: StepStatus.Done,
        sub: 'Completed · Jul 7, 2026 09:52 — Generated 3 corrective options: (A) Re-calibrate FT-2204, (B) Apply back-allocation factor 0.916, (C) Swap to backup meter FT-2205.',
      },
      {
        number: 6,
        title: 'Approval & Decision',
        actor: ActorType.Human,
        status: StepStatus.Current,
        sub: '⏳ Awaiting decision · Assigned to R. Torres (Production Supervisor) · Due Jul 8, 2026 17:00 — Select corrective option and authorise allocation adjustment.',
      },
      {
        number: 7,
        title: 'Remediation & Close-out',
        actor: ActorType.System,
        status: StepStatus.Pending,
        sub: 'Not started — AI Agent will apply approved allocation corrections in PI/SCADA; Engineer countersigns & archives audit trail.',
      },
    ],
  },
  {
    id: 'moc-2024-044',
    title: 'Process Change · Train 2',
    badge: WorkflowBadgeStatus.New,
    description: 'MOC-2024-044 · Process Change · Train 2',
    status: WorkflowBadgeStatus.New,
    steps: [
      {
        number: 1,
        title: 'Change Request Submitted',
        actor: ActorType.Human,
        status: StepStatus.Done,
        sub: 'Completed · Jun 28, 2026 — Change request for Train 2 separator pressure set-point adjustment submitted.',
      },
      {
        number: 2,
        title: 'Risk Assessment',
        actor: ActorType.AIAgent,
        status: StepStatus.Done,
        sub: 'Completed · Jun 29, 2026 — Low risk. No critical safety systems impacted.',
      },
      {
        number: 3,
        title: 'Engineer Approval',
        actor: ActorType.Human,
        status: StepStatus.Current,
        sub: '⏳ In Progress — Awaiting sign-off from Lead Process Engineer.',
      },
      {
        number: 4,
        title: 'Implementation & Verification',
        actor: ActorType.System,
        status: StepStatus.Pending,
        sub: 'Not started — Apply set-point change and verify operation within 24 h.',
      },
    ],
  },
  {
    id: 'inspection-iw-887',
    title: 'Safety Audit · Q3',
    badge: WorkflowBadgeStatus.New,
    description: 'Inspection #IW-887 · Safety Audit · Q3',
    status: WorkflowBadgeStatus.New,
    actionText:
      'Complete Q3 safety audit findings review and sign off on corrective action items identified in sections 4.2 and 7.1 of the audit report.',
    steps: [
      {
        number: 1,
        title: 'Audit Execution',
        actor: ActorType.Human,
        status: StepStatus.Done,
        sub: 'Completed · Jul 1, 2026 — Q3 safety walkthrough completed by HSE team.',
      },
      {
        number: 2,
        title: 'Findings Compilation',
        actor: ActorType.AIAgent,
        status: StepStatus.Done,
        sub: 'Completed · Jul 2, 2026 — 12 observations compiled, 3 classified as high priority.',
      },
      {
        number: 3,
        title: 'Peer Review',
        actor: ActorType.Human,
        status: StepStatus.Waiting,
        sub: 'Waiting on HSE Supervisor sign-off before proceeding.',
      },
      {
        number: 4,
        title: 'Management Approval',
        actor: ActorType.Human,
        status: StepStatus.Pending,
        sub: 'Not started.',
      },
    ],
  },
  {
    id: 'permit-2024-0079',
    title: 'Confined Space · Tank 7',
    badge: WorkflowBadgeStatus.Closed,
    description: 'Permit #2024-0079 · Confined Space Entry · Tank 7',
    status: WorkflowBadgeStatus.Closed,
    steps: [
      {
        number: 1,
        title: 'Permit Issued',
        actor: ActorType.Human,
        status: StepStatus.Done,
        sub: 'Completed · Jun 15, 2026 — Confined space entry permit issued and signed.',
      },
      {
        number: 2,
        title: 'Entry Completed',
        actor: ActorType.Human,
        status: StepStatus.Done,
        sub: 'Completed · Jun 15, 2026 — Inspection completed without incident.',
      },
      {
        number: 3,
        title: 'Permit Close-out',
        actor: ActorType.Human,
        status: StepStatus.Done,
        sub: 'Completed · Jun 15, 2026 — Permit closed. All personnel accounted for.',
      },
    ],
  },
  {
    id: 'hazop-2024-012',
    title: 'Risk Assessment · Unit 6',
    badge: WorkflowBadgeStatus.AwaitingDecision,
    description: 'HAZOP-2024-012 · Hazard Operability Study · Unit 6',
    status: WorkflowBadgeStatus.AwaitingDecision,
    actionText:
      'Review HAZOP study recommendations for Unit 6 revamp. Approve implementation of safeguarding measures SG-01 through SG-04 and allocate budget for instrumentation upgrades.',
    steps: [
      {
        number: 1,
        title: 'HAZOP Study',
        actor: ActorType.System,
        status: StepStatus.Done,
        sub: 'Completed · Jul 5, 2026 — Multi-disciplinary HAZOP session completed. 24 action items identified.',
      },
      {
        number: 2,
        title: 'Risk Ranking',
        actor: ActorType.AIAgent,
        status: StepStatus.Done,
        sub: 'Completed · Jul 6, 2026 — 4 high-risk items require immediate safeguarding measures.',
      },
      {
        number: 3,
        title: 'Management Decision',
        actor: ActorType.Human,
        status: StepStatus.Current,
        sub: '⏳ Awaiting decision — Operations Manager to approve safeguarding budget.',
      },
      {
        number: 4,
        title: 'Implementation',
        actor: ActorType.System,
        status: StepStatus.Pending,
        sub: 'Not started.',
      },
    ],
  },
];

// -----------------------------------------------------------------------------
// Full exception + Glass-Box RCA report (OKA_PP Gas Deviation — 2026-05-16).
// Structured like the Exception Report page mock (EXCEPTIONS_MOCK.exceptions[n]).
// Ported verbatim from the "Okan Gas Deviation · 2026-05-16 · Glass-Box RCA
// (v2-withPI)" investigation. Provenance tooltips use the `{{tt:DISPLAY|Label:
// value|...}}` token format understood by the report panel.
// -----------------------------------------------------------------------------
export const OKA_PP_MAY16_EXCEPTION_MOCK: ExceptionItem = {
  exceptionId: 'EXC-2026-0002',
  idNumber: '0002',
  title: 'OKA_PP Gas Deviation — 2026-05-16 · Glass-Box RCA (v2-withPI)',
  well: {
    name: 'OKAN Platform (OKA_PP)',
    stream: 'OKA_GGCP_M_TGAS',
    product: 'Gas export',
    area: 'Nigeria Mid-Africa (NMA)',
    supplementary: 'Okan Field · OKA_PP platform · GGCP aggregate gas metering · offshore',
  },
  exceptionType: 'Export Volume Deviation',
  useCase: 'UC1',
  importanceLevel: 'Medium',
  confidenceLevel: 90,
  occurred: '2026-05-16',
  detected: '2026-07-24 00:54',
  status: 'active',
  summary:
    'Gas export dropped 6.38% on 2026-05-16 vs the prior day. NET and THEOR moved together (1.45% gap) — a physical-loss-dominant event, not an allocation/factor event.',
  evidence:
    '100,442.96 → 94,032.91 (Δ −6,410.05 / −6.38%); THEOR Δ −4.93%; NET−THEOR gap 1.45%; OKA-143-W surface flowline leak 21,124 mscf/d shut in May-15 → May-18.',
  recommendedAction:
    'No proposed actions. The deviation is largely explained by the OKA-143-W flowline leak and corroborated by the real-time pressure data check. Work order at PF109-JKT-30 likely be relevant because OKA-143 flows to JKT-30.',
  timeline: [
    { state: 'Detected', at: '2026-07-24 00:54 CST' },
    { state: 'Agent analysis started', at: '2026-07-24 00:55 CST' },
    { state: 'Report generated', at: '2026-07-24 01:02 CST' },
  ],
  workflowSteps: [
    {
      workflowId: 'WF-0002',
      stepNumber: 1,
      title: 'Step 1 - Export Gas Trend Check',
      actorType: 'AI Agent',
      source: 'dbo.rv_strm_day_alloc.NET_VOL',
      completedBy: 'Ontology Agent',
      completedAt: '2026-07-24 00:55 CST',
      summary:
        'Checked OKA_GGCP_M_TGAS from 2026-05-07 to 2026-05-25. On May 16, NET and THEOR dropped together (−6.38% vs −4.93%), pointing to a real field production impact rather than a reporting-only shift. Flow rebounded on May 17 (+8.11% NET).',
      blocks: [
        {
          type: 'table',
          headers: ['Prod Day', 'NET_VOL', 'Δ NET', '% NET', 'THEOR', '% THEOR', 'Diagnosis'],
          rows: [
            ['2026-05-07', '99,863.00', '−865.65', '−0.86%', '101,509.56', '+1.22%', 'drop · factor-driven (NET<<THEOR)'],
            ['2026-05-08', '101,242.58', '+1,379.58', '+1.38%', '100,324.01', '−1.17%', 'recovery / stable'],
            ['2026-05-09', '98,497.95', '−2,744.63', '−2.71%', '99,424.90', '−0.90%', 'drop · factor-driven (NET<<THEOR)'],
            ['2026-05-10', '98,369.21', '−128.74', '−0.13%', '99,263.65', '−0.16%', 'drop · both dropped'],
            ['2026-05-11', '100,909.84', '+2,540.63', '+2.58%', '100,645.13', '+1.39%', 'recovery / stable'],
            ['2026-05-12', '97,801.40', '−3,108.44', '−3.08%', '97,835.67', '−2.79%', 'drop · both dropped'],
            ['2026-05-13', '105,099.58', '+7,298.18', '+7.46%', '104,080.68', '+6.38%', 'recovery / stable'],
            ['2026-05-14', '106,955.23', '+1,855.65', '+1.77%', '103,340.84', '−0.71%', 'recovery / stable'],
            ['2026-05-15', '100,442.96', '−6,512.27', '−6.09%', '101,118.38', '−2.15%', 'drop · factor-driven (NET<<THEOR)'],
            ['<b>2026-05-16</b>', '<b>94,032.91</b>', '<b>−6,410.05</b>', '<b>−6.38%</b>', '96,132.26', '−4.93%', 'ANCHOR · NET & THEOR drop together = physical event'],
            ['2026-05-17', '101,654.31', '+7,621.40', '+8.11%', '101,774.46', '+5.87%', 'recovery / stable'],
            ['2026-05-18', '104,700.71', '+3,046.40', '+3.00%', '103,518.12', '+1.71%', 'recovery / stable'],
            ['2026-05-19', '106,438.18', '+1,737.47', '+1.66%', '104,359.99', '+0.81%', 'recovery / stable'],
            ['2026-05-20', '105,411.05', '−1,027.13', '−0.96%', '104,853.97', '+0.47%', 'drop · factor-driven (NET<<THEOR)'],
            ['2026-05-21', '98,588.89', '−6,822.16', '−6.47%', '100,455.28', '−4.20%', 'drop · factor-driven (NET<<THEOR)'],
            ['2026-05-22', '99,017.11', '+428.22', '+0.43%', '98,137.29', '−2.31%', 'recovery / stable'],
            ['2026-05-23', '100,670.61', '+1,653.50', '+1.67%', '100,165.56', '+2.07%', 'recovery / stable'],
            ['2026-05-24', '101,476.46', '+805.85', '+0.80%', '103,066.27', '+2.90%', 'recovery / stable'],
            ['2026-05-25', '103,714.77', '+2,238.31', '+2.21%', '105,960.66', '+2.81%', 'recovery / stable'],
          ],
        },
        {
          type: 'paragraph',
          html: 'On May-16, NET and THEOR moved down together, with only about {{tt:1.45%|Calc: −6.38 − (−4.93)}} difference and a similar movement ratio of about {{tt:1.3×|Calc: 6.38 / 4.93}}. This pattern points to a field production impact dominated by well-side loss rather than a reporting-only shift.',
        },
      ],
    },
    {
      workflowId: 'WF-0002',
      stepNumber: 2,
      title: 'Step 2 - Platform and Feeder Check',
      actorType: 'AI Agent',
      source: 'dbo.rv_strm_day_alloc.VOL_FACTOR',
      completedBy: 'Ontology Agent',
      completedAt: '2026-07-24 00:56 CST',
      summary:
        'Compared PP/ST1/ST2 feeder gas and saw the same VOL_FACTOR behavior across all three platforms. Factor stayed low during the upset period, then recovered from May 17 to May 18 (0.926 to 1.004) as wells came back after the OKA-143-W leak repair.',
      blocks: [
        {
          type: 'table',
          headers: ['Day', 'OKA_PP', 'OKA_ST1', 'OKA_ST2', 'Σ feeders', 'VOL_FACTOR (all 3)'],
          rows: [
            ['2026-05-08', '39,677.03', '2,292.35', '27,170.21', '69,139.59', '0.7628'],
            ['2026-05-09', '38,962.79', '2,251.08', '26,681.11', '67,894.98', '0.7491'],
            ['2026-05-10', '38,968.13', '2,251.39', '26,684.77', '67,904.29', '0.7492'],
            ['2026-05-11', '39,832.81', '2,301.35', '27,276.88', '69,411.04', '0.7658'],
            ['2026-05-12', '38,924.44', '2,248.86', '26,654.84', '67,828.14', '0.7484'],
            ['2026-05-13', '42,703.96', '2,475.41', '23,743.37', '68,922.74', '0.8238'],
            ['2026-05-14', '43,311.97', '2,510.66', '24,081.42', '69,904.05', '0.8355'],
            ['2026-05-15', '41,280.96', '2,392.93', '22,952.18', '66,626.07', '0.7963'],
            ['<b>2026-05-16</b>', '<b>41,873.53</b>', '2,418.88', '23,201.09', '67,493.50', '<b>0.8050</b>'],
            ['2026-05-17', '48,007.37', '2,782.83', '26,692.06', '77,482.26', '0.9261'],
            ['2026-05-18', '52,039.86', '3,016.59', '28,934.12', '83,990.57', '1.0039'],
            ['2026-05-19', '51,052.30', '2,959.34', '28,385.04', '82,396.68', '0.9848'],
            ['2026-05-20', '50,480.23', '2,926.18', '28,066.97', '81,473.38', '0.9738'],
            ['2026-05-21', '46,835.60', '2,714.91', '26,040.56', '75,591.07', '0.9035'],
            ['2026-05-22', '45,770.46', '2,653.68', '25,453.25', '73,877.39', '0.8831'],
          ],
        },
        {
          type: 'paragraph',
          html: 'VOL_FACTOR was {{tt:0.8050|Source: dbo.rv_strm_day_alloc.VOL_FACTOR|Day: 2026-05-16}} on the anchor day, slightly above May 15 (0.7963). It then recovered strongly from May 17 to May 18 (0.926 -> 1.004), which lines up with completion of the OKA-143-W flowline leak repair on May 18. This matches a physical-loss pattern where deferred wells were captured in THEOR and recovery came from wells returning to service.',
        },
      ],
    },
    {
      workflowId: 'WF-0002',
      stepNumber: 3,
      title: 'Step 3 - Well Downtime Check for 2026-05-16',
      actorType: 'AI Agent',
      source: 'dbo.ECKERNEL_NMA_RV_WELL_DEFERMENT',
      completedBy: 'Ontology Agent',
      completedAt: '2026-07-24 00:58 CST',
      summary:
        'OKA-143-W surface flowline leak dominates at 21,124 mscf/d — shut in the entire anchor day. Additional: OKA-127h-W jacket ESD (triggered by OKA-143), OKA-61D-W 8 h ≈ 991 mscf. Baseline losses (OKA-139h-W SCSSV, OKA-69D-W log-up) persist unchanged.',
      blocks: [
        {
          type: 'table',
          headers: ['Well', 'Start', 'End', 'Event', 'Reason', 'Gas loss (mscf/d)', 'Comment', 'Impact on May 16 Δ'],
          rows: [
            ['OKA-143-W', '2026-05-15 00:00', '2026-05-18 00:00', 'DOWN', 'T1_PIPE', '21,124.16', 'Surface flowline leak — well shut in whole anchor window.', '↓ MAJOR: ~21,124 mscf/d × 2.0 d spanning May-16'],
            ['OKA-127h-W', '2026-05-15 11:00', '2026-05-16 12:00', 'DOWN', 'T1_PIPE', 'NULL', 'SI on Jkt ESD due to leak on OK-143 flowline. BOL.', '↓ Jkt ESD ripple from OKA-143 leak; unquantified'],
            ['OKA-69D-W', '2026-05-06 09:00', '2026-05-28 04:00', 'DOWN', '(none)', '6,793.79', 'Logged up. Reactivated BOL.', '↓ baseline −6,794 mscf/d (unchanged day-over-day)'],
            ['OKA-139h-W', '2026-05-01 12:00', '(open)', 'DOWN', 'T1_GEN', '4,612.92', 'SCSSV Fault (persistent).', '↓ baseline −4,613 mscf/d (not the trigger)'],
            ['OKA-61D-W', '2026-05-16 04:00', '2026-05-16 12:00', 'DOWN', '(none)', '2,973.73', 'Logged up, BOL.', '↓ ~991 mscf on May 16 (8h × 2,974/24)'],
            ['OKA-34D-W', '2026-05-14 09:00', '2026-05-14 14:00', 'DOWN', '(none)', '2,842.56', 'SI to test OK-151, BOL.', 'recovered before May 16 anchor'],
            ['OKA-57D-W', '2026-05-08 14:00', '2026-05-14 14:00', 'DOWN', '(none)', '1,241.40', 'Logged up. Reactivated, BOL.', 'recovered before May 16 anchor'],
            ['OKA-57D-W', '2026-05-17 04:00', '2026-05-17 14:00', 'DOWN', '(none)', '1,241.40', 'Found recycling gas, BOL.', 'post-anchor (May-17)'],
            ['OKA-127h-W', '2026-05-15 11:00', '(open)', 'DOWN', 'T1_GEN', 'NULL', 'SI due to Jkt ESD (second row, T1_GEN cause).', 'baseline Jkt ESD continues'],
          ],
        },
        {
          type: 'paragraph',
          html: '<b>Physical-loss accounting for May 16:</b> {{tt:OKA-143-W surface flowline leak|Source: dbo.ECKERNEL_NMA_RV_WELL_DEFERMENT.COMMENTS}} dominates at {{tt:21,124 mscf/d|Source: GAS_LOSS_RATE_MSCFPERDAY}} — shut in the entire anchor day. OKA-143 alone is 3.3× the observed 6,410 mscf NET drop; because the leak also fed the THEOR calc, the NET−THEOR gap stays small (1.45%).',
        },
      ],
    },
    {
      workflowId: 'WF-0002',
      stepNumber: 4,
      title: 'Step 5 - Pressure Data Check (PI, real data)',
      actorType: 'AI Agent',
      source: 'PITelemetryHistory (Kusto · IO_Surface_EventHouse)',
      completedBy: 'Ontology Agent',
      completedAt: '2026-07-24 01:00 CST',
      summary:
        'Reviewed hourly flowline pressure for the six involved wells using current PI history. OKA-143-W and OKA-127h-W were flat/silent on May 16, consistent with shut-in. OKA-139h-W showed recovery trend, and OKA-57D-W stepped up after May 17. No evidence of a field-wide pressure surge.',
      blocks: [
        {
          type: 'table',
          headers: ['Well', 'TimeSeriesId', 'Day', 'avg (psi)', 'min', 'max', 'n samples'],
          rows: [
            ['OKA-143-W', 'nma-prod/105793', '2026-05-14', '—', '—', '—', '0 · silent'],
            ['OKA-143-W', 'nma-prod/105793', '2026-05-15', '370.8', '370.8', '370.8', '30'],
            ['OKA-143-W', 'nma-prod/105793', '<b>2026-05-16</b>', '—', '—', '—', '0 · silent'],
            ['OKA-143-W', 'nma-prod/105793', '2026-05-17', '—', '—', '—', '0 · silent'],
            ['OKA-127h-W', 'nma-prod/38026', '2026-05-14', '—', '—', '—', '0 · silent'],
            ['OKA-127h-W', 'nma-prod/38026', '2026-05-15', '209.1', '209.1', '209.1', '30'],
            ['OKA-127h-W', 'nma-prod/38026', '<b>2026-05-16</b>', '—', '—', '—', '0 · silent'],
            ['OKA-127h-W', 'nma-prod/38026', '2026-05-17', '—', '—', '—', '0 · silent'],
            ['OKA-139h-W', 'nma-prod/112479', '2026-05-14', '85.1', '82.8', '86.6', '144'],
            ['OKA-139h-W', 'nma-prod/112479', '2026-05-15', '87.7', '84.9', '90.6', '153'],
            ['OKA-139h-W', 'nma-prod/112479', '<b>2026-05-16</b>', '94.4', '90.8', '97.2', '138'],
            ['OKA-139h-W', 'nma-prod/112479', '2026-05-17', '98.7', '96.9', '101.5', '144'],
            ['OKA-61D-W', 'nma-prod/105847', '2026-05-14', '118.3', '97.1', '137.2', '144'],
            ['OKA-61D-W', 'nma-prod/105847', '2026-05-15', '113.3', '96.2', '137.7', '150'],
            ['OKA-61D-W', 'nma-prod/105847', '<b>2026-05-16</b>', '117.4', '91.4', '134.2', '138'],
            ['OKA-61D-W', 'nma-prod/105847', '2026-05-17', '125.2', '122.8', '126.9', '144'],
            ['OKA-57D-W', 'nma-prod/38074', '2026-05-14', '148.4', '135.2', '155.1', '141'],
            ['OKA-57D-W', 'nma-prod/38074', '2026-05-15', '147.7', '145.6', '149.9', '141'],
            ['OKA-57D-W', 'nma-prod/38074', '<b>2026-05-16</b>', '147.4', '145.0', '149.0', '141'],
            ['OKA-57D-W', 'nma-prod/38074', '2026-05-17', '157.5', '145.4', '171.5', '141'],
            ['OKA-69D-W', 'nma-prod/108914', '2026-05-14', '—', '—', '—', '0 · silent'],
            ['OKA-69D-W', 'nma-prod/108914', '2026-05-15', '—', '—', '—', '0 · silent'],
            ['OKA-69D-W', 'nma-prod/108914', '<b>2026-05-16</b>', '—', '—', '—', '0 · silent'],
            ['OKA-69D-W', 'nma-prod/108914', '2026-05-17', '—', '—', '—', '0 · silent'],
          ],
        },
        {
          type: 'paragraph',
          html: '<b>✓ PI verified.</b> Fresh horizon probe: {{tt:max(Timestamp) = 2026-07-25T00:53:57Z|Source: PITelemetryHistory | summarize max(Timestamp), count()}} · n = 306,516,223,297 rows (LIVE, ~2 min lag). Only 6 of 39 OKA_PP wells deviated, each matching its own Recipe-C row; OKA-143-W and OKA-127h-W are the only wells silent on May 16 (shut-in, not sensor failure). No field-wide backpressure spike → H4 (plant compression) firmly ruled out. ClientRequestId GBAGENT_PIsweep_May16_v2PI (413 rows) + GBAGENT_PIsweep_May16_involved_hourly (295 rows).',
        },
      ],
    },
    {
      workflowId: 'WF-0002',
      stepNumber: 5,
      title: 'Step 4 - Likely Cause Ranking',
      actorType: 'AI Agent',
      source: 'Fabric ontology run 2026-07-24',
      completedBy: 'Ontology Agent',
      completedAt: '2026-07-24 01:01 CST',
      summary:
        'Ranked likely causes with 90% confidence. Primary cause is the OKA-143-W surface flowline leak. A field-wide compression upset was ruled out based on PI pressure checks.',
      blocks: [
        {
          type: 'hypotheses',
          items: [
            {
              rank: 1,
              tag: 'primary',
              title: 'H1 · OKA-143-W surface flowline leak (physical, multi-day)',
              body: 'Recipe C row OKA-143-W shows DOWN 2026-05-15 00:00 → 2026-05-18 00:00 at 21,124 mscf/d, reason T1_PIPE, comment "Surface flowline leak". On the anchor day the well is SI for all 24 hours = 21,124 mscf of physical loss — 3.3× the observed 6,410 mscf aggregate drop. Because the deferment feeds THEOR, %THEOR (−4.93%) tracks %NET (−6.38%) with only a 1.45% gap. PI corroborates: FLOWLINEPRESSURE froze at 370.8 psi on May 15 then went silent May 16 → May 17. Recovery: END_DATE = 2026-05-18 coincides with VOL_FACTOR returning to 1.0039.',
            },
            {
              rank: 2,
              tag: 'partial',
              title: 'H2 · OKA-61D-W trip (secondary physical, minor)',
              body: 'OKA-61D-W DOWN 04:00 → 12:00 May 16 at 2,974 mscf/d = ~991 mscf additional physical loss. Comment "Logged up, BOL"; reactivated same-day. PI shows normal cycling ~106 psi through 10:00Z, then a step-up to ~124 psi from 13:00Z (brought back on at a slightly higher backpressure). Additive to H1 and consistent with the 1.45% NET−THEOR gap.',
            },
            {
              rank: 3,
              tag: 'partial',
              title: 'H3 · Persistent baseline losses (OKA-69D-W log-up + OKA-139h-W SCSSV)',
              body: 'OKA-69D-W DOWN May 06 → May 28 at 6,794 mscf/d (log-up) and OKA-139h-W SCSSV fault at 4,613 mscf/d open since May 01. Both persist across the window with no day-over-day change → they explain the depressed VOL_FACTOR baseline (0.75–0.85) but cannot cause the anchor-day delta. Combined baseline ≈ −11,407 mscf/d.',
            },
            {
              rank: 4,
              tag: 'ruled',
              title: 'H4 · Field-wide gas compression / EGP pipeline restriction ✖',
              body: 'RULED OUT (PI-verified). The Recipe F PI sweep returns no field-wide coordinated backpressure spike: 33 of 39 wells sit inside their normal 3–4 psi bands through May 14 → May 17. Only the 5 wells with matching Recipe-C rows deviate, each for its own deferment reason. Rule 7 verdict negative — the co-drop in oil (−2.77%) and condensate (−4.60%) is explained by OKA-143 being a producer with matching product streams, not a plant restriction.',
            },
          ],
        },
      ],
    },
    {
      workflowId: 'WF-0002',
      stepNumber: 6,
      title: 'Step 6 - Maintenance Work Order Check (Okan, PF109-*)',
      actorType: 'AI Agent',
      source: 'dbo.work_order_flat_v1_euw',
      completedBy: 'Ontology Agent',
      completedAt: '2026-07-24 01:01 CST',
      summary:
        'No matching PF109 work order landed exactly on May 16. A nearby item, PF109-JKT-30 flange leak work on May 17, may be relevant because OKA-143 flows to JKT-30.',
      blocks: [
        {
          type: 'table',
          headers: ['Effective', 'Equipment', 'WO #', 'Status', 'Priority', 'Description'],
          rows: [
            ['2026-05-08', 'PF190-PDG-1000', '1225486', 'Reviewed Complete', 'Routine', 'ICE ITPM - FW PUMP'],
            ['2026-05-08', 'PF190-BUILDING-101', '1228931', 'Ready for Approval', 'Routine', 'Refurbish Okan Lab Caravan'],
            ['2026-05-10', 'PF190-BUILDING-101', '1229288', 'Daily Work in Progress', 'Low Priority', 'Rectify Leaking Deck Plate'],
            ['2026-05-11', 'PF190-JKT-QP1', '1229424', 'Job Planning', 'Routine', 'Repair HLO Office AC unit'],
            ['2026-05-17', 'PF109-JKT-30', '1230655', 'Daily Work in Progress', 'Urgent-Schedule Break In', 'Tighten Flange Leak (post-anchor day)'],
            ['2026-05-21', 'PF190-PAX-510A', '1231500', 'Reviewed Complete', 'Routine', 'Fail to start of P/L Pump A'],
            ['2026-05-21', 'PF190-DAN-504A', '1231498', 'Reviewed Complete', 'Routine', 'Generator dropping off load'],
          ],
        },
      ],
    },
    {
      workflowId: 'WF-0002',
      stepNumber: 7,
      title: 'Engineer review & validation',
      actorType: 'Human',
      source: 'Production / Surface Engineering',
      completedBy: null,
      completedAt: null,
      summary:
        'No additional corrective actions proposed. Confirm close-out rationale: deviation is explained by the OKA-143-W flowline leak and corroborated by real-time pressure behavior.',
    },
    {
      workflowId: 'WF-0002',
      stepNumber: 8,
      title: 'Disposition & close-out',
      actorType: 'Human',
      source: 'Surface IO',
      completedBy: null,
      completedAt: null,
      summary:
        'Close the exception as explained by physical-loss findings and PI corroboration, noting PF109-JKT-30 as a potentially relevant follow-on work order because OKA-143 flows to JKT-30.',
    },
  ],
  report: {
    confidenceStatement:
      'High confidence — NET and THEOR moved together (1.45% gap), the deferment ledger quantifies a dominant OKA-143-W flowline leak, and real PI telemetry corroborates the shut-in timeline while ruling out a field-wide compression fault.',
    sections: [
      {
        id: 'summary',
        navLabel: 'Summary',
        title: 'Executive Summary',
        blocks: [
          {
            type: 'paragraph',
            html:
              'On the {{tt:2026-05-16|Source: user prompt}} anchor day, Okan gas export <code>OKA_GGCP_M_TGAS</code> dropped {{tt:−6,410 mscf (−6.38%)|Source: dbo.rv_strm_day_alloc.NET_VOL|Calc: 94,032.91 − 100,442.96}} vs the prior day. Crucially, <b>THEOR moved almost the same amount</b> ({{tt:−4.93%|Source: dbo.rv_strm_day_alloc.THEOR_NET_VOL}}), giving only a {{tt:1.45%|Calc: pct_net − pct_theor}} NET-vs-THEOR gap. Per Rule 2 this is a <b>physical-loss-dominant event</b>, not an allocation/factor event. The Recipe C deferment table reveals an {{tt:OKA-143-W surface flowline leak|Source: dbo.ECKERNEL_NMA_RV_WELL_DEFERMENT.COMMENTS}} at {{tt:21,124 mscf/d|Source: GAS_LOSS_RATE_MSCFPERDAY}} — shut in for the entire anchor window May-15 → May-18. Compounding: the leak triggered a jacket ESD on <code>OKA-127h-W</code>, and <code>OKA-61D-W</code> tripped for 8 h on May 16 (~991 mscf). May-17 rebound (+8.11% NET) and full recovery to VOL_FACTOR {{tt:1.0039|Day: 2026-05-18}} on May 18 confirms operational return-to-line. <b>Recipe F PI sweep executed for real this run</b>: OKA-143-W FLOWLINEPRESSURE shows a flat 370.8 psi frozen at May 15 with no telemetry for May-16 or May-17 — a classic post-shut-in "silent sensor" pattern. A field-wide coordinated backpressure spike (Rule 7) is <b>not present</b> — confirming a well-local physical event, not a plant compression fault.',
          },
          {
            type: 'metrics',
            items: [
              { label: 'May 16 NET Δ day-over-day', value: '−6,410 mscf (−6.38%)', tt: 'Calc: 94,032.91 − 100,442.96' },
              { label: 'NET vs THEOR gap (May 16)', value: '−1.45%', tt: 'Calc: pct_net(−6.38) − pct_theor(−4.93) — small divergence = physical event' },
              { label: 'May 16 NET vs May 13–14 baseline', value: '−11,994 mscf', tt: 'Calc: 94,033 − 106,027 avg(13,14)' },
              { label: 'May 17 rebound vs May 16', value: '+7,621 mscf (8.11%)', tt: 'Calc: 101,654 − 94,033' },
              { label: 'VOL_FACTOR May 16 vs May 18 healthy', value: '−19.9%', tt: 'Calc: 0.8050 → 1.0039' },
              { label: 'OKA-143-W flowline leak loss rate', value: '21,124 mscf/d', tt: 'Source: GAS_LOSS_RATE_MSCFPERDAY' },
            ],
          },
        ],
      },
      {
        id: 'naming',
        navLabel: 'Session Naming',
        title: 'Session-Discovered Naming (this run)',
        blocks: [
          {
            type: 'table',
            headers: ['Concept', 'Value found this session', 'Source'],
            rows: [
              ['Anchor date', '<code>2026-05-16</code> (fresh RCA — v2-withPI)', 'User prompt · Recipe A'],
              ['Product / stream', 'Gas · <code>OKA_GGCP_M_TGAS</code>', 'Runbook §🚩'],
              ['Facility', '<code>OKA_PP</code> (via feeder decomposition)', 'Recipe B'],
              ['Ontology grounding', 'ontology_grounding.json (99 KB) — 11 entities, 13 relationships, 12 bindings', 'fabriciq-ontology-consumption-cli'],
              ['Join key WELL_ON_JACKET', '<code>Well.WELL_JKT → Jacket.JACKET_CODE</code>', 'ontology semanticEnrichment (306051210520341207)'],
              ['Join key LOCATED_AT', '<code>Equipment.OP_FCTY_1_CODE → Facility.CODE</code>', 'ontology semanticEnrichment (965164092623477049)'],
              ['Join key PITELEMETRY_MEASURES', '<code>PITelemetry.TimeSeriesId → pi_tag_well_xref.TimeSeriesId</code> AND <code>Well.CODE → pi_tag_well_xref.EC_AssetCode</code>', 'ontology semanticEnrichment (639900282185829203)'],
              ['MEASURES (Equipment ↔ PI)', 'No contextualization decoded → "pending customer crosswalk"', 'ontology (303062954637971785)'],
              ['PI TagName pattern (Okan wells)', '<code>NMA.WMS.OK-&lt;n&gt;.FLOWLINEPRESSURE</code>', 'F1 sample rows'],
              ['Kusto PITelemetryHistory horizon', '<code>2026-07-25T00:53:57Z</code> — LIVE, ~2 min behind wall clock; 306.5 B rows back to 2025-09-05', 'global tail probe'],
              ['Work-order effective-date column', '<code>work_order_effective_date_time</code>', 'INFORMATION_SCHEMA discovery'],
              ['Well ↔ Facility column', '<code>ECKERNEL_NMA_OV_WELL.OP_FCTY_1_CODE</code>', 'Empirical this session'],
            ],
          },
        ],
      },
      {
        id: 'workflow',
        navLabel: 'Workflow',
        title: 'Workflow',
        blocks: [{ type: 'workflow' }],
      },
      {
        id: 'action-item',
        navLabel: 'Recommended Actions',
        title: 'Recommended Actions',
        blocks: [
          {
            type: 'paragraph',
            html:
              'No proposed actions for this event. The May-16 deviation is largely explained by the OKA-143-W flowline leak and corroborated by the real-time pressure data check. Work order <code>PF109-JKT-30</code> may be relevant because OKA-143 flows to JKT-30.',
          },
          { type: 'source', text: 'dbo.rv_strm_day_alloc · dbo.ECKERNEL_NMA_RV_WELL_DEFERMENT · PITelemetryHistory · Fabric ontology run 2026-07-24' },
        ],
      },
      {
        id: 'data-gaps',
        navLabel: 'Data Gaps',
        title: 'Data Gaps',
        blocks: [
          {
            type: 'list',
            items: [
              'Equipment PI (Recipe F.C) — MEASURES relationship still has no contextualization in ontology_grounding.json (pending customer crosswalk). TagPrefix fallback NMA.SOUTH.OKAN.PP.* not exercised this run.',
              'OKA_NAG_D_TGAS / OKA_NWP_D_TGAS feeders inactive in current dataset vintage — not the trigger, noted.',
              'OKA-143-W flowline leak WO — not surfaced by the surface_equipment_code filter. Recommend a follow-up query WHERE work_order_description LIKE \'%OK-143%\'.',
              'GraphQL / projections — not GA; explicit SQL joins used per skill routing table.',
              'Cross-source semantic joins — MEASURES and PITELEMETRY_MEASURES require agent-side stitching per skill routing rules.',
            ],
          },
        ],
      },
      {
        id: 'ontology',
        navLabel: 'Ontology Traversal',
        title: 'Ontology Graph Traversal',
        blocks: [
          {
            type: 'list',
            items: [
              'Facility[OKA_PP] (637341148937081114) → dbo.ECKERNEL_NMA_OV_FCTY_CLASS_1',
              'JACKET_ON_FACILITY (239578654782002325): Jacket.FACILITY_CODE → Facility.CODE (ctx dbo.okanjacket)',
              'WELL_ON_JACKET (306051210520341207): Well.WELL_JKT → Jacket.JACKET_CODE (ctx dbo.ECKERNEL_NMA_OV_WELL, filter OP_FCTY_1_CODE=\'OKA_PP\')',
              'PITELEMETRY_MEASURES (639900282185829203): composite PITelemetry.TimeSeriesId → pi_tag_well_xref.TimeSeriesId AND Well.CODE → pi_tag_well_xref.EC_AssetCode',
              'PITelemetry (942711682229755717): LakehouseTable dbo.pi_telemetry_points + KustoTable PITelemetryHistory (413 rows — real data)',
              'LOCATED_AT (965164092623477049): Equipment.OP_FCTY_1_CODE → Facility.CODE (ctx dbo.ECKERNEL_NMA_OV_EQPM)',
              'FLOWS_TO (799239337601599506) / FLOWS_FROM (106327258165389798): Stream ↔ Facility (ctx dbo.ECKERNEL_NMA_OV_STREAM)',
              'IMPACTS (837969789807371542): WellDownTimeEvent (1116328613072396559) → Well (ctx dbo.rv_well_deferment_keyed)',
              'MEASURES (303062954637971785): Equipment → PI — no contextualization; TagPrefix fallback not exercised',
            ],
          },
          { type: 'source', text: 'ontology_grounding.json · fabriciq-ontology-consumption-cli · 51 parts decoded from getDefinition' },
        ],
      },
      {
        id: 'timings',
        navLabel: 'Query Timings',
        title: 'Query Timings (baseline)',
        blocks: [
          {
            type: 'table',
            headers: ['Engine', 'Label / ClientRequestId', 'Purpose (Runbook rule)', 'Duration', 'Rows', 'StatementID'],
            rows: [
              ['SQL', 'GBAGENT_RecipeA_OKA_May16_v2PI', 'Recipe A · 18-day gas signal (Rule 2/4/5)', '4,379 ms', '60', '{77815538-128A-43C1-9795-AF9044FC201B}'],
              ['SQL', 'GBAGENT_RecipeB_gas_feeders_May16_v2PI', 'Recipe B · feeder VOL_FACTOR (Rule 1/9)', '168 ms', '45', '{8E4ACB02-180B-4457-8B42-F21D8A7A537B}'],
              ['SQL', 'GBAGENT_RecipeC_defer_May16_v2PI', 'Recipe C · active deferments (Rule 6)', '2,401 ms', '12', '{30E1845A-4563-4633-9A21-E6C79DAF0DBE}'],
              ['SQL', 'GBAGENT_WO_OKA_May16_v2PI', 'Recipe D · anchor-day work orders (Rule 3)', '3,830 ms', '7', '{B8011A46-D5DF-45D5-B47B-4748859B4BFB}'],
              ['SQL', 'GBAGENT_F1_may16_involved', 'F1 · PI tag resolve for 6 involved wells', '417 ms', '6', '{D9CCA694-B041-40DE-9AE8-B5E55468E108}'],
              ['Kusto', 'GBAGENT_backfill_check_2026-07-24', 'Horizon probe · PITelemetryHistory max/min', '380 ms', '1', 'GBAGENT_backfill_check_2026-07-24'],
              ['Kusto', 'GBAGENT_PIsweep_May16_v2PI', 'Recipe F · F2 daily PI sweep (45 tags × 12 days)', '997 ms', '413', 'GBAGENT_PIsweep_May16_v2PI'],
              ['Kusto', 'GBAGENT_PIsweep_May16_involved_hourly', 'Recipe F · F2 hourly PI (6 wells × 4 days)', '100 ms', '295', 'GBAGENT_PIsweep_May16_involved_hourly'],
            ],
          },
          { type: 'source', text: 'SQL subtotal 11,195 ms · Kusto subtotal 1,477 ms · Total query wall-clock 12,672 ms (12.67 s) — server-side execution only' },
        ],
      },
    ],
  },
};

export const OKA_PP_APR07_EXCEPTION_MOCK: ExceptionItem = {
  exceptionId: 'EXC-2026-0003',
  idNumber: '0003',
  title: 'OKA_PP Gas Deviation - 2026-04-07 - Glass-Box RCA (v3-with106h)',
  well: {
    name: 'OKAN Platform (OKA_PP)',
    stream: 'OKA_GGCP_M_TGAS',
    product: 'Gas export',
    area: 'Nigeria Mid-Africa (NMA)',
    supplementary: 'Okan Field · OKA_PP platform · GGCP aggregate gas metering · offshore',
  },
  exceptionType: 'Export Volume Deviation',
  useCase: 'UC1',
  importanceLevel: 'Medium',
  confidenceLevel: 88,
  occurred: '2026-04-07',
  detected: '2026-07-29 09:00',
  status: 'active',
  summary:
    'Gas export dropped 6.09% on 2026-04-07 vs prior day. NET vs THEOR diverged by ~5.16%, indicating an allocation/VOL_FACTOR reset as the dominant mechanism. Added OKA-106h pressure analysis confirms a concurrent well-state change and strengthens secondary physical context.',
  evidence:
    '118,308.15 -> 111,104.47 (delta -7,203.68 / -6.09%); THEOR delta -0.93%; NET-THEOR gap ~5.16%; GGCP VOL_FACTOR 1.0492 -> 0.9945 -> 1.0249; event-day deferments (excluding chronic OKA-72-W) ~1,650 mscf/d; OKA-106h shut-in signature starts ~08:41 on Apr-07.',
  recommendedAction:
    'Allocation-factor evidence indicates this deviation is an allocation artifact rather than a real production drop, and no further deviation investigation is warranted; A clear pressure impact was found on OKA-106h reflecting downtime that is not captured in EC downtime events. A downtime entry should be recorded to capture this.',
  timeline: [
    { state: 'Detected', at: '2026-07-29 09:00 CST' },
    { state: 'Agent analysis started', at: '2026-07-29 09:05 CST' },
    { state: 'Report generated', at: '2026-07-29 09:32 CST' },
  ],
  workflowSteps: [
    {
      workflowId: 'WF-0003',
      stepNumber: 1,
      title: 'Step 1 - Export Gas Trend Check',
      actorType: 'AI Agent',
      source: 'dbo.rv_strm_day_alloc.NET_VOL',
      completedBy: 'Ontology Agent',
      completedAt: '2026-07-29 09:07 CST',
      summary:
        'Checked OKA_GGCP_M_TGAS around Apr-07: NET dropped -6.09% while THEOR moved -0.93% (gap ~5.16%). This indicates most movement came from metering/allocation effects, with only part from well downtime.',
      blocks: [
        {
          type: 'table',
          headers: ['Stream', 'Product', 'NET Apr-06', 'NET Apr-07', 'Delta NET', '% NET', '% THEOR', 'Diagnosis'],
          rows: [
            ['OKA_GGCP_M_TGAS', 'Gas', '118,308.15', '111,104.47', '-7,203.68', '-6.09%', '-0.93%', 'Anchor drop with large NET-THEOR divergence'],
            ['OKA_GS_D_TOIL', 'Oil', '25,497', '24,739', '-758', '-2.97%', '-1.28%', 'Secondary movement'],
            ['OKA_GGCP_D_COND', 'Condensate', '942', '1,073', '+130', '+13.81%', '+16.27%', 'Positive, not event driver'],
          ],
        },
        {
          type: 'paragraph',
          html: 'On Apr-07, NET dropped much more than THEOR (~5.16% gap), which points to allocation/factor effects as the main driver rather than a pure production-loss event.',
        },
      ],
    },
    {
      workflowId: 'WF-0003',
      stepNumber: 2,
      title: 'Step 2 - Platform and Feeder Check',
      actorType: 'AI Agent',
      source: 'dbo.rv_strm_day_alloc.VOL_FACTOR',
      completedBy: 'Ontology Agent',
      completedAt: '2026-07-29 09:09 CST',
      summary:
        'PP/ST1/ST2 feeder streams moved together on VOL_FACTOR, pointing to a common gas-handling or accounting shift, not a single-well failure.',
      blocks: [
        {
          type: 'table',
          headers: ['Entity', 'Day', 'NET_VOL', 'THEOR_NET_VOL', 'VOL_FACTOR', 'Signal'],
          rows: [
            ['GGCP aggregate (OKA_GGCP_M_TGAS)', '2026-04-06', '118,308.15', '112,764.18', '1.0492', 'Pre-event factor high'],
            ['GGCP aggregate (OKA_GGCP_M_TGAS)', '<b>2026-04-07</b>', '<b>111,104.47</b>', '111,720.56', '<b>0.9945</b>', 'Anchor-day factor dip'],
            ['GGCP aggregate (OKA_GGCP_M_TGAS)', '2026-04-08', '113,883.83', '111,114.32', '1.0249', 'Recovery'],
            ['OKA_PP_D_TGAS_XS1S2', '2026-04-07', '43,442', '50,665', '0.8574', 'Uniform feeder factor'],
            ['OKA_ST1_D_TGAS', '2026-04-07', '3,516', '4,100', '0.8574', 'Uniform feeder factor'],
            ['OKA_ST2_D_TGAS', '2026-04-07', '28,181', '32,866', '0.8574', 'Uniform feeder factor'],
          ],
        },
        {
          type: 'paragraph',
          html: 'PP, ST1, and ST2 all showed the same feeder factor behavior, which is consistent with a system-wide back-allocation/reconciliation effect rather than a single-well issue.',
        },
      ],
    },
    {
      workflowId: 'WF-0003',
      stepNumber: 3,
      title: 'Step 3 - Well Downtime Check for 2026-04-07',
      actorType: 'AI Agent',
      source: 'dbo.ECKERNEL_NMA_RV_WELL_DEFERMENT',
      completedBy: 'Ontology Agent',
      completedAt: '2026-07-29 09:12 CST',
      summary:
        'Well deferments on the event day were real but too small to explain the full gas shortfall. OKA-72-W remained a known background loss, not the day trigger.',
      blocks: [
        {
          type: 'table',
          headers: ['Well', 'Start', 'End', 'Gas loss (mscf/d)', 'Comment', 'Impact on Apr-07 delta'],
          rows: [
            ['OKA-72-W', '2026-03-23', '2026-04-22', '19,201', 'Ruptured flowline (chronic)', 'Baseline only; ruled out as day-over-day trigger'],
            ['OKA-120-W', '2026-04-06 07:00', '2026-04-07 12:00', '708', 'Logged up, BOL', 'Partial'],
            ['OKA-24-W', '2026-04-07 10:00', '2026-04-07 11:00', '340', 'SI for choke change', 'Minor'],
            ['OKA-121D-W', '2026-04-06 07:00', '2026-04-07 12:00', '339', 'Logged up, BOL', 'Partial'],
            ['OKA-19-W', '2026-04-06 19:00', '2026-04-07 09:00', '264', 'SI to test OK-106', 'Secondary; links to 106h analysis'],
          ],
        },
        {
          type: 'paragraph',
          html: 'Non-chronic deferments total about 1,650 mscf/d, around 23% of the 7,204 mscf aggregate drop, so they explain part of the movement but not the full deviation.',
        },
      ],
    },
    {
      workflowId: 'WF-0003',
      stepNumber: 4,
      title: 'Step 4 - Pressure Data Check (PI/Kusto)',
      actorType: 'AI Agent',
      source: 'PITelemetryHistory (Kusto · IO_Surface_EventHouse)',
      completedBy: 'Ontology Agent',
      completedAt: '2026-07-29 09:20 CST',
      summary:
        'Multiple wells showed an Apr-07 flowline pressure step-up, consistent with downstream route/compression restriction and aligned with the observed factor behavior.',
      blocks: [
        {
          type: 'table',
          headers: ['Well', 'TimeSeriesId', 'Apr-06 avg psi', 'Apr-07 avg psi', 'Apr-08 avg psi', 'Apr-09 avg psi', 'Signal'],
          rows: [
            ['OK-100H', 'nma-prod/37980', '245.2', '284.4', '283.2', '143.2', '+16% Apr-07 spike'],
            ['OK-121D', 'nma-prod/112911', '124.6', '147.5', '157.0', '160.7', '+18% Apr-07 and elevated'],
            ['OK-24', 'nma-prod/113145', '158.8', '189.6', '140.2', '158.9', '+19% one-day spike'],
            ['OK-19', 'nma-prod/112996', '1,208.7', '1,310.4', '1,256.9', '1,213.9', '+8% Apr-07 spike'],
            ['OK-105H', 'nma-prod/112435', '183.0', '184.1', '182.8', '183.4', 'Flat; route likely isolated'],
            ['OK-72', 'nma-prod/113090', '779.7', '742.4', '702.9', '665.5', 'Declining chronic signature'],
          ],
        },
        {
          type: 'paragraph',
          html: 'Several wells showed coordinated pressure elevation on Apr-07, which is consistent with a downstream handling restriction and matches the observed VOL_FACTOR behavior.',
        },
      ],
    },
    {
      workflowId: 'WF-0003',
      stepNumber: 5,
      title: 'Step 5 - OKA-106h Pressure Impact Analysis',
      actorType: 'AI Agent',
      source: '106g-pressure_profile.csv / OKA-106h-W_pressure_impact_analysis.md',
      completedBy: 'Ontology Agent',
      completedAt: '2026-07-29 09:16 CST',
      summary:
        'OKA-106h pressure profile shows clear shut-in/intervention behavior starting around 08:41 on Apr-07, giving direct field timing evidence for downtime impact.',
      blocks: [
        {
          type: 'table',
          headers: ['Window', 'THP', 'CHP', 'FLP', 'Interpretation'],
          rows: [
            ['Apr-07 00:10-08:11', '277 -> 290 psi', '~1,152 psi steady', '159-166 psi steady', 'Normal flowing state'],
            ['Apr-07 ~08:41', 'Drops to ~-1.4 psi', '~1,152 psi', '~165 psi', 'Shut-in/isolation onset'],
            ['Apr-07 10:12-11:12', '~ -1.5 flat', '1,153 -> 1,400 psi', '~164 psi', 'Tubing shut-in with casing build-up'],
            ['Apr-07 11:41-12:11', '~ -1.4', '92 transient -> 1,176 psi', '158 -> 213 psi spike', 'Valve operations and flowline transient'],
            ['Apr-07 ~16:00-21:00', '~ -1.2', 'Bleed-down to ~137-142 psi', '85-115 psi swings', 'Controlled intervention behavior'],
            ['Apr-08 (all day)', '~ -1.2 to -1.4', '~1,120-1,148 then partial blanks', '83-205 psi', 'Still shut-in; no clear return-to-flow'],
          ],
        },
        {
          type: 'list',
          items: [
            'Corroborates SI-to-test-OK-106 timing with a measured shut-in onset near 08:41 Apr-07.',
            'Supports flowline backpressure/transient narrative as a secondary physical mechanism.',
            'Highlights likely under-count of 106h own lost contribution in deferment-only accounting.',
            'Does not overturn top-line cause because pressure-only data does not directly quantify MSCF volume loss.',
          ],
        },
        {
          type: 'paragraph',
          html: 'The OKA-106h pressure profile adds clear, time-stamped field evidence of the shut-in/intervention sequence and improves traceability while keeping the allocation-dominant conclusion intact.',
        },
      ],
    },
    {
      workflowId: 'WF-0003',
      stepNumber: 6,
      title: 'Step 6 - Likely Cause Ranking',
      actorType: 'AI Agent',
      source: 'Integrated SQL + Kusto + 106h analysis',
      completedBy: 'Ontology Agent',
      completedAt: '2026-07-29 09:24 CST',
      summary:
        'Main driver remains metering/allocation reset, with partial physical contributions supported by deferments and OKA-106h pressure evidence.',
      blocks: [
        {
          type: 'hypotheses',
          items: [
            {
              rank: 1,
              tag: 'primary',
              title: 'H1 · GGCP VOL_FACTOR / allocation reset (dominant)',
              body: 'NET-THEOR divergence (~5.16%) plus synchronized factor movement (1.0492 -> 0.9945 -> 1.0249) indicate dominant allocation-side effect.',
            },
            {
              rank: 2,
              tag: 'partial',
              title: 'H2 · EGP compressor intervention as triggering context',
              body: 'Same-day compressor work orders and PI pressure signature are consistent with temporary downstream handling restriction.',
            },
            {
              rank: 3,
              tag: 'partial',
              title: 'H3 · Event-day well deferments (OKA-120/121D/24/19)',
              body: 'Combined ~1,650 mscf/d contributes to physical component but does not explain aggregate magnitude alone.',
            },
            {
              rank: 4,
              tag: 'partial',
              title: 'H4 · OKA-106h test/shut-in pressure signature (added evidence)',
              body: 'Apr-07 08:41 shut-in and transient profile provide direct physical timing corroboration and improve causal traceability.',
            },
            {
              rank: 5,
              tag: 'ruled',
              title: 'H5 · Chronic OKA-72-W rupture as day trigger',
              body: 'Ruled out as trigger because it is persistent baseline loss active before and after anchor day.',
            },
          ],
        },
      ],
    },
    {
      workflowId: 'WF-0003',
      stepNumber: 7,
      title: 'Step 7 - Recommended Field Actions',
      actorType: 'AI Agent',
      source: 'Integrated evidence and control actions',
      completedBy: 'Ontology Agent',
      completedAt: '2026-07-29 09:26 CST',
      summary:
        'Action close-out focuses on capturing missing downtime evidence and aligning field event records with what pressure data shows.',
      blocks: [
        {
          type: 'list',
          ordered: true,
          items: [
            'Confirm whether Apr-07 GGCP factor dip was expected reconciliation or operational anomaly in production accounting.',
            'Review closure notes and exact event windows for PG200-CGT-5250D and PG200-CGT-5250B work orders.',
            'Add OKA-106h as explicit secondary contributor in deferment-review checklist and RCA templates.',
            'Maintain pressure-coherence watchlist for Okan gas route wells to detect future downstream restrictions early.',
            'Reassess Apr-09 NET re-dip (-9.75%) for recurrence versus delayed recovery effects.',
          ],
        },
      ],
    },
    {
      workflowId: 'WF-0003',
      stepNumber: 8,
      title: 'Engineer review & validation',
      actorType: 'Human',
      source: 'Production / Surface Engineering',
      completedBy: null,
      completedAt: null,
      summary:
        'Confirm allocation-factor evidence supports an allocation artifact versus a real production drop and that no further deviation investigation is warranted; then record a downtime entry for the clear OKA-106h pressure-impact window that is not captured in EC downtime events.',
    },
    {
      workflowId: 'WF-0003',
      stepNumber: 9,
      title: 'Disposition & close-out',
      actorType: 'Human',
      source: 'Surface IO',
      completedBy: null,
      completedAt: null,
      summary: 'Accept, reassign, or dismiss based on allocation-dominant findings and secondary physical corroboration package.',
    },
  ],
  report: {
    confidenceStatement:
      'High confidence in allocation/factor-dominant mechanism (NET-THEOR divergence + synchronized factor movement), with medium-high confidence in secondary physical contributors including newly integrated OKA-106h pressure-state evidence.',
    sections: [
      {
        id: 'summary',
        navLabel: 'Summary',
        title: 'Executive Summary',
        blocks: [
          {
            type: 'paragraph',
            html:
              'On the 2026-04-07 anchor day, Okan gas export <code>OKA_GGCP_M_TGAS</code> dropped by about 7,204 mscf (-6.09%) day-over-day. Because THEOR moved only -0.93%, the ~5.16% NET-THEOR divergence indicates that most observed movement is allocation/factor-side. The added OKA-106h pressure analysis confirms a concurrent shut-in/intervention signature at ~08:41 and improves physical explainability without replacing the dominant factor-reset conclusion.',
          },
          {
            type: 'metrics',
            items: [
              { label: 'Apr-07 NET delta day-over-day', value: '-7,203.68 mscf (-6.09%)', tt: 'Calc: 111,104.47 - 118,308.15' },
              { label: 'Apr-07 THEOR delta day-over-day', value: '-0.93%', tt: 'Calc: (111,720.56 - 112,764.18) / 112,764.18' },
              { label: 'NET vs THEOR gap', value: '~5.16%', tt: 'Calc: 6.09 - 0.93' },
              { label: 'GGCP VOL_FACTOR movement', value: '1.0492 -> 0.9945 -> 1.0249', tt: 'Apr-06 to Apr-08 profile' },
              { label: 'Event-day deferments (non-chronic)', value: '~1,650 mscf/d', tt: '708 + 340 + 339 + 264' },
              { label: 'OKA-106h shut-in onset', value: '~08:41 (Apr-07)', tt: 'Pressure profile state transition' },
            ],
          },
        ],
      },
      {
        id: 'naming',
        navLabel: 'Session Naming',
        title: 'Session-Discovered Naming (this run)',
        blocks: [
          {
            type: 'table',
            headers: ['Concept', 'Value found this session', 'Source'],
            rows: [
              ['Anchor date', '<code>2026-04-07</code>', 'Recipe A'],
              ['Product / stream', 'Gas · <code>OKA_GGCP_M_TGAS</code>', 'Runbook start point'],
              ['Facility', '<code>OKA_PP</code>', 'Recipe B'],
              ['Pressure-impact add-on', '<code>OKA-106h-W</code> telemetry from 106g-pressure_profile.csv', 'OKA-106h-W_pressure_impact_analysis.md'],
              ['Work-order context', '<code>PG200-CGT-5250D</code> and <code>PG200-CGT-5250B</code>', 'dbo.work_order_flat_v1_euw'],
              ['PI context', '<code>PITelemetryHistory</code> multi-well flowline pressure', 'Kusto query set'],
            ],
          },
        ],
      },
      {
        id: 'workflow',
        navLabel: 'Workflow',
        title: 'Workflow',
        blocks: [{ type: 'workflow' }],
      },
      {
        id: 'action-item',
        navLabel: 'Recommended Actions',
        title: 'Recommended Actions',
        blocks: [
          {
            type: 'list',
            ordered: true,
            items: [
              'Validate accounting intent for Apr-07 GGCP factor reset and retain traceability in exception metadata.',
              'Correlate compressor work-order execution windows with factor dip/recovery windows.',
              'Standardize a dedicated pressure-impact step (as done for OKA-106h) for similar events.',
              'Maintain PI-based route-coherence watch to detect downstream constraints before large NET-THEOR divergence appears.',
            ],
          },
          { type: 'source', text: 'dbo.rv_strm_day_alloc · dbo.ECKERNEL_NMA_RV_WELL_DEFERMENT · PITelemetryHistory · 106g-pressure_profile.csv · RCA 2026-04-07' },
        ],
      },
      {
        id: 'data-gaps',
        navLabel: 'Data Gaps',
        title: 'Data Gaps',
        blocks: [
          {
            type: 'list',
            items: [
              'OKA-106h dataset is pressure/temperature only; direct MSCF loss needs flow-rate integration from allocation/deferment tables.',
              'Some facility-to-equipment lineage remains dependent on cross-workspace ontology contextualization.',
              'Chronic-vs-event decomposition still requires strict business-rule handling at report generation time.',
            ],
          },
        ],
      },
      {
        id: 'ontology',
        navLabel: 'Ontology Traversal',
        title: 'Ontology Graph Traversal',
        blocks: [
          {
            type: 'list',
            items: [
              'Facility[OKA_PP] -> Well / Stream / Equipment traversal used as RCA backbone.',
              'WELL_ON_JACKET and LOCATED_AT relationship chains support well/facility/equipment stitching.',
              'PITELEMETRY_MEASURES relationship bridges Well to PI telemetry through time-series mappings.',
              'Pressure-impact evidence for OKA-106h is treated as a supplemental physical-validation branch in the workflow.',
            ],
          },
        ],
      },
      {
        id: 'timings',
        navLabel: 'Query Timings',
        title: 'Query Timings (baseline)',
        blocks: [
          {
            type: 'table',
            headers: ['Engine', 'Label', 'Purpose', 'Duration', 'Rows'],
            rows: [
              ['SQL', 'Recipe A pull', 'Aggregate NET/THEOR signal', 'baseline', 'stream-window rows'],
              ['SQL', 'Recipe B pull', 'Feeder factor check', 'baseline', 'feeder-window rows'],
              ['SQL', 'Recipe C pull', 'Deferment overlap and rates', 'baseline', 'deferment rows'],
              ['Kusto', 'PI sweep', 'Multi-well flowline pressure corroboration', 'baseline', 'daily/hourly PI rows'],
              ['File analysis', '106h profile review', 'State-change and transient identification', 'baseline', 'raw telemetry rows'],
            ],
          },
          {
            type: 'source',
            text: 'Timings remain representative baseline values from prior RCA runs; production pipeline can replace with exact runtime telemetry if required.',
          },
        ],
      },
    ],
  },
};


// -----------------------------------------------------------------------------
// Main-page display mocks derived from the OKA_PP exception RCAs.
// Each exception feeds the "exception" portion (detail header + proposed action);
// the derived WorkflowItem feeds the "details" portion (workflow steps). These are
// the items shown in the Workflow Inbox ("Exception Inbox") main page.
// -----------------------------------------------------------------------------
const ACTOR_TYPE_TO_ENUM: Record<string, ActorType> = {
  'AI Agent': ActorType.AIAgent,
  Human: ActorType.Human,
  System: ActorType.System,
};

const EXCEPTION_STATUS_TO_BADGE: Record<ExceptionItem['status'], WorkflowBadgeStatus> = {
  active: WorkflowBadgeStatus.New,
  'awaiting-decision': WorkflowBadgeStatus.AwaitingDecision,
  'awaiting-others': WorkflowBadgeStatus.New,
  resolved: WorkflowBadgeStatus.Closed,
};

/** Builds the Workflow Inbox item shown for a given exception RCA. */
function buildWorkflowFromException(exception: ExceptionItem, title: string): WorkflowItem {
  const steps = exception.workflowSteps ?? [];
  const firstIncomplete = steps.findIndex(step => !step.completedAt);
  const badge = EXCEPTION_STATUS_TO_BADGE[exception.status] ?? WorkflowBadgeStatus.New;

  return {
    id: exception.exceptionId,
    title,
    badge,
    description: `${exception.exceptionId} · ${exception.exceptionType} · ${exception.well.stream}`,
    createdDate: exception.occurred,
    updatedDate: exception.detected,
    status: badge,
    actionText: exception.recommendedAction,
    summary: exception.summary,
    identity: {
      title: exception.title,
      area: exception.well.area,
      supplementary: exception.well.supplementary,
      stream: exception.well.stream,
      product: exception.well.product,
      exceptionType: exception.exceptionType,
      useCase: exception.useCase,
      confidenceLevel: exception.confidenceLevel,
      confidenceStatement: exception.report?.confidenceStatement ?? exception.summary,
    },
    steps: steps.map((step, index) => ({
      number: step.stepNumber,
      title: step.title,
      actor: ACTOR_TYPE_TO_ENUM[step.actorType] ?? ActorType.System,
      status: step.completedAt
        ? StepStatus.Done
        : index === firstIncomplete
          ? StepStatus.Current
          : StepStatus.Pending,
      sub: step.summary ?? '',
      source: step.source ?? null,
      completedBy: step.completedBy ?? null,
      completedOn: step.completedAt ?? null,
      blocks: step.blocks,
    })),
  };
}

/** Exception shown in the main page's exception portion (retained for compatibility). */
export const MAIN_PAGE_EXCEPTION_MOCK: ExceptionItem = OKA_PP_MAY16_EXCEPTION_MOCK;

/** WorkflowItem derived from the 2026-05-16 exception. */
export const MAIN_PAGE_WORKFLOW_MOCK: WorkflowItem = buildWorkflowFromException(
  OKA_PP_MAY16_EXCEPTION_MOCK,
  'OKA_PP Gas Deviation · 2026-05-16'
);

/** WorkflowItem derived from the 2026-04-07 exception. */
export const OKA_PP_APR07_WORKFLOW_MOCK: WorkflowItem = buildWorkflowFromException(
  OKA_PP_APR07_EXCEPTION_MOCK,
  'OKA_PP Gas Deviation · 2026-04-07'
);
